/**
 * Alderon Games — browser Sentry bootstrap (template asset).
 *
 * Apps copy this file into `resources/js/sentry-browser.mjs` and call
 * `initSentry({ app })` from `resources/js/app.js` before mounting:
 *
 *   import { initSentry } from './sentry-browser.mjs';
 *   initSentry({ app });
 *
 * Requires `@sentry/vue` in the app's package.json. The browser SDK's
 * browserTracingIntegration auto-reads the <meta name="sentry-trace"> +
 * <meta name="baggage"> tags emitted by the @sentryTraceMeta Blade directive
 * (Alderon\Base\Sentry\TraceMeta) so the browser transaction continues the
 * server-side W3C trace visible in Tempo/Grafana.
 *
 * Disabled entirely (Sentry.init never called) when VITE_SENTRY_DSN is empty,
 * though @sentry/vue is still bundled — apps that never use Sentry can drop
 * this file and the import to exclude it from the bundle.
 *
 * @param {{ app: import('vue').App, router?: import('vue-router').Router }} options
 */
import * as Sentry from '@sentry/vue';

export function initSentry({ app, router } = {}) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (! dsn) {
        return;
    }

    Sentry.init({
        app,
        ...(router ? { router } : {}),
        dsn,
        environment: import.meta.env.VITE_APP_ENV ?? 'local',
        release: import.meta.env.VITE_APP_BUILD ?? undefined,
        integrations: [
            Sentry.browserTracingIntegration({
                ...(router ? { router } : {}),
            }),
        ],
        tracesSampleRate: resolveSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, import.meta.env.VITE_APP_ENV),
        // Only inject sentry-trace/baggage on calls to our own backend (where the
        // PHP side normalises them onto the W3C traceparent) plus sentry.io.
        tracePropagationTargets: ['localhost', /^\//, /^https:\/\/[^/]+\.sentry\.io/],
    });
}

function resolveSampleRate(raw, env) {
    const parsed = parseFloat(raw);

    if (! Number.isNaN(parsed)) {
        return clamp01(parsed);
    }

    return 'production' === env ? 0.1 : 1.0;
}

function clamp01(value) {
    if (0 > value) {
        return 0;
    }

    if (1 < value) {
        return 1;
    }

    return value;
}